import Phaser from 'phaser';
import { GAME } from '../config';
import type { FactionId } from '../../shared/types';
import { loadProgress, saveProgress, getSelectedHeroId } from '../../shared/progress';
import {
  META_HERO_TREES,
  metaTreeFor,
  metaNodeLevel,
  setMetaNodeLevel,
  type MetaStatNode,
} from '../progression/meta-shop';
import {
  metaNodeCostAtLevel,
  metaNodeTotalCost,
  metaRespecRefund,
} from '../progression/meta-economy';
import { BASE_PALETTES, type BasePalette } from './meta-base-art';

/**
 * MetaShopScene — 「指揮官基地」永久升級畫面(AI 基地圖版).
 *
 * boss 2026-06-15 改版:基地升級畫面換用 AI 生的門派基地圖(取代之前手畫的醜向量)。
 *   - 中央主視覺 = 該門派的 `base-<faction>.png`(觀星塔/符文神殿/齒輪廠/生態圓頂/黃金要塞)。
 *   - 「買升級 = 基地長大」改成在圖上做整體 glow-up:**完成度越高 → 基地越亮 + 發光更強**,
 *     配完成度條;升級時冒星光粒子(granular 部件省略,改整體發光提升)。
 *   - 升級節點改成**圖旁的清單面板**(節點名 / 每級加成 / 成本 / 購買鈕;
 *     買不起灰、滿級顯「已滿」)。
 *
 * 雙軌進度鐵則:只讀寫玩遊戲軌 metaCoins / metaShop(經 meta-shop / meta-economy
 *   純函式),**絕不**碰 factionXP / freeXP / skillLevels / 修煉值。本檔只換呈現層,
 *   **不動 catalog / 經濟 / 天花板邏輯**(meta-shop.ts / meta-economy.ts 鎖定 + 有測試守):
 *   沿用既有 loadProgress → buy → setMetaNodeLevel → saveProgress。
 *
 * 進場/退場:`scene.start('MetaShopScene', { heroId, returnScene })`;返回鈕
 *   dispatch `ef:meta-shop-back` 並回 returnScene(預設 CampaignMapScene;車庫進來時為 GarageScene)。
 */
const HERO_ORDER: readonly FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];

/** base-<faction>.png 的 texture key(本場景自帶 preload)。 */
const BASE_IMG_KEY: Readonly<Record<FactionId, string>> = {
  celestial: 'meta-base-celestial',
  runic:     'meta-base-runic',
  techno:    'meta-base-techno',
  elemental: 'meta-base-elemental',
  dominion:  'meta-base-dominion',
};
const BASE_IMG_URL: Readonly<Record<FactionId, string>> = {
  celestial: 'assets/ai-gen/garage/base-celestial.png',
  runic:     'assets/ai-gen/garage/base-runic.png',
  techno:    'assets/ai-gen/garage/base-techno.png',
  elemental: 'assets/ai-gen/garage/base-elemental.png',
  dominion:  'assets/ai-gen/garage/base-dominion.png',
};

interface MetaShopData {
  heroId?: FactionId;
  returnScene?: string;
}

/** stat → 中文短名(節點清單用)。 */
const STAT_LABEL: Readonly<Record<MetaStatNode['stat'], string>> = {
  damage: '傷害',
  maxHp: '最大生命',
  moveSpeed: '移動速度',
  luck: '幸運',
};

export class MetaShopScene extends Phaser.Scene {
  private heroId: FactionId = 'celestial';
  private returnScene = 'CampaignMapScene';

  // 持久 UI 元件(切門派時 refresh)。
  private coinsText: Phaser.GameObjects.Text | null = null;
  private tabContainer: Phaser.GameObjects.Container | null = null;
  /** 中央基地主視覺(AI 圖 + glow + 完成度條),切門派 / 購買後重建。 */
  private baseContainer: Phaser.GameObjects.Container | null = null;
  /** 右側節點清單面板,切門派 / 購買後重建。 */
  private listContainer: Phaser.GameObjects.Container | null = null;

  constructor() { super('MetaShopScene'); }

  preload(): void {
    // 5 門派基地 AI 圖。缺檔 → buildBase 走色塊 fallback(向後相容,headless 安全)。
    try {
      for (const hero of HERO_ORDER) {
        if (!this.textures.exists(BASE_IMG_KEY[hero])) {
          this.load.image(BASE_IMG_KEY[hero], BASE_IMG_URL[hero]);
        }
      }
    } catch { /* headless */ }
  }

  create(data?: MetaShopData): void {
    const progress = loadProgress();
    this.heroId = data?.heroId
      ?? (progress ? getSelectedHeroId(progress) : 'celestial');
    this.returnScene = data?.returnScene ?? 'CampaignMapScene';

    const W = GAME.width;
    const H = GAME.height;

    // 全域底色(深夜紫藍,對齊 title-scene / 車庫視覺語言)。
    this.cameras.main.setBackgroundColor('#0a0420');
    this.drawAmbientBackdrop(W, H);

    // 標題列
    this.add.text(W / 2, 30, '指揮官基地', {
      fontSize: '32px', color: '#ffe082', fontFamily: 'serif', fontStyle: 'bold',
      stroke: '#3d1a66', strokeThickness: 7,
    }).setOrigin(0.5).setShadow(0, 3, '#1a0533', 8, true, true);
    this.add.text(W / 2, 58, '升級節點 → 基地越蓋越亮、越壯', {
      fontSize: '13px', color: '#b7a6e0', fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    // 頂部金幣牌(金色醒目)
    this.buildCoinBadge(W);

    // 返回鈕(左上)
    this.buildCornerButton(64, 30, 96, 38, '‹ 返回', 0x37474f, 0x90a4ae, () => {
      try { window.dispatchEvent(new CustomEvent('ef:meta-shop-back')); } catch { /* headless */ }
      this.gotoReturnScene();
    });

    // 洗點鈕(右下)
    this.buildCornerButton(W - 92, H - 32, 150, 42, '↺ 洗點 (退70%)', 0x4a148c, 0xab47bc, () => this.respec());

    // 5 門派頁籤
    this.buildHeroTabs(W);

    // 中央基地主視覺(AI 圖)+ 右側節點清單。
    this.buildBase();
    this.buildList();
  }

  // ── 全域背景:深空漸層 + 微光,不喧賓奪主(基地才是主角)──────────────────
  private drawAmbientBackdrop(W: number, H: number): void {
    const g = this.add.graphics();
    const top = Phaser.Display.Color.ValueToColor('#140a2e');
    const bot = Phaser.Display.Color.ValueToColor('#06091e');
    const steps = 40;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const c = Phaser.Display.Color.Interpolate.ColorWithColor(top, bot, 100, t * 100);
      g.fillStyle(Phaser.Display.Color.GetColor(c.r, c.g, c.b), 1);
      g.fillRect(0, (H / steps) * i, W, H / steps + 1);
    }
    g.setDepth(-100);
    // 中央地面暖光(基地坐落處)
    const glow = this.add.graphics();
    glow.fillStyle(0x6a3fb0, 0.1);
    glow.fillEllipse(W * 0.38, H * 0.72, W * 0.6, 200);
    glow.setDepth(-99);
  }

  // ── 頂部金幣牌 ────────────────────────────────────────────────────────────
  private buildCoinBadge(W: number): void {
    const x = W - 86;
    const y = 30;
    const w = 132;
    const h = 40;
    const g = this.add.graphics();
    g.fillStyle(0x1c1330, 0.95);
    g.fillRoundedRect(x - w / 2, y - h / 2, w, h, 20);
    g.lineStyle(2, 0xffd54f, 0.8);
    g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 20);
    g.setDepth(50);
    this.add.text(x - w / 2 + 18, y, '🪙', { fontSize: '20px', fontFamily: 'sans-serif' })
      .setOrigin(0.5).setDepth(51);
    this.coinsText = this.add.text(x - w / 2 + 38, y, '0', {
      fontSize: '20px', color: '#ffd54f', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(51);
    this.updateCoinText();
  }

  private updateCoinText(): void {
    const p = loadProgress();
    const coins = p && Number.isFinite(p.metaCoins) ? (p.metaCoins as number) : 0;
    if (this.coinsText) this.coinsText.setText(String(coins));
  }

  private currentCoins(): number {
    const p = loadProgress();
    return p && Number.isFinite(p.metaCoins) ? (p.metaCoins as number) : 0;
  }

  // ── 5 門派頁籤(每門派用自己的主題色;active 高亮 + 上浮)──────────────────
  private buildHeroTabs(W: number): void {
    if (this.tabContainer) { this.tabContainer.destroy(); this.tabContainer = null; }
    const container = this.add.container(0, 0).setDepth(40);
    this.tabContainer = container;

    const tabW = 70;
    const tabH = 56;
    const gap = 12;
    const total = tabW * HERO_ORDER.length + gap * (HERO_ORDER.length - 1);
    const startX = W / 2 - total / 2 + tabW / 2;
    const y = 100;

    HERO_ORDER.forEach((hero, i) => {
      const x = startX + i * (tabW + gap);
      const active = hero === this.heroId;
      const pal = BASE_PALETTES[hero];

      const g = this.add.graphics();
      const yOff = active ? -4 : 0;
      // 卡片
      g.fillStyle(active ? pal.primary : 0x241a3e, active ? 0.95 : 0.8);
      g.fillRoundedRect(x - tabW / 2, y - tabH / 2 + yOff, tabW, tabH, 12);
      g.lineStyle(active ? 3 : 1.5, active ? pal.light : 0x4a3d6b, active ? 1 : 0.7);
      g.strokeRoundedRect(x - tabW / 2, y - tabH / 2 + yOff, tabW, tabH, 12);
      if (active) {
        // active 底部發光條
        g.fillStyle(pal.glow, 0.9);
        g.fillRoundedRect(x - tabW / 2 + 10, y + tabH / 2 + yOff - 5, tabW - 20, 4, 2);
      }
      container.add(g);

      const emoji = this.add.text(x, y - 8 + yOff, pal.emoji, {
        fontSize: '24px', fontFamily: 'sans-serif',
      }).setOrigin(0.5);
      container.add(emoji);
      const name = this.add.text(x, y + 16 + yOff, this.heroShortName(hero), {
        fontSize: '11px', color: active ? '#fff8e1' : '#9080b8', fontFamily: 'sans-serif',
        fontStyle: active ? 'bold' : 'normal',
      }).setOrigin(0.5);
      container.add(name);

      // 命中區(整張卡)。
      const hit = this.add.zone(x, y + yOff, tabW, tabH).setOrigin(0.5);
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => this.switchHero(hero));
      container.add(hit);
    });
  }

  private heroShortName(hero: FactionId): string {
    switch (hero) {
      case 'celestial': return '星算門';
      case 'runic':     return '符文門';
      case 'techno':    return '機械門';
      case 'elemental': return '自然門';
      case 'dominion':  return '疆域門';
    }
  }

  private switchHero(hero: FactionId): void {
    if (this.heroId === hero) return;
    this.heroId = hero;
    this.buildHeroTabs(GAME.width);
    this.buildBase();
    this.buildList();
  }

  // ── 完成度:已蓋等級總和 / 總可蓋級數(0..1)。驅動基地亮度/發光 ──────────────
  private completionRatio(): { lvSum: number; lvMax: number; pct: number } {
    const tree = metaTreeFor(this.heroId);
    const progress = loadProgress();
    let lvSum = 0;
    let lvMax = 0;
    for (const node of tree.nodes) {
      lvSum += progress ? metaNodeLevel(progress, this.heroId, node.id) : 0;
      lvMax += node.maxLevel;
    }
    const pct = lvMax > 0 ? Math.round((lvSum / lvMax) * 100) : 0;
    return { lvSum, lvMax, pct };
  }

  // ── 中央基地主視覺:AI 圖 + 完成度 glow-up + 完成度條 ─────────────────────────
  //
  // 版面:左半畫面放基地圖(右半留給節點清單)。完成度越高 → 圖越亮(tint 由暗轉白)
  //   + 後方發光圈更強 + 升滿冒「滿」標。純呈現,不碰任何 progress 數值。
  private buildBase(): void {
    if (this.baseContainer) { this.baseContainer.destroy(); this.baseContainer = null; }

    const W = GAME.width;
    const H = GAME.height;
    // 基地置於左半中央(右半 x≈ W*0.6+ 給清單)。
    const cx = W * 0.30;
    const cy = H * 0.56;

    const container = this.add.container(0, 0).setDepth(10);
    this.baseContainer = container;

    const pal = BASE_PALETTES[this.heroId];
    const { pct } = this.completionRatio();
    const ratio = pct / 100;

    // 展示艙地台(讓基地像坐在發光平台上)。
    const pad = this.add.graphics();
    pad.fillStyle(pal.dark, 0.5);
    pad.fillEllipse(cx, cy + 116, 300, 64);
    pad.fillStyle(pal.primary, 0.35 + ratio * 0.35);
    pad.fillEllipse(cx, cy + 116, 230, 48);
    pad.lineStyle(2.5, pal.glow, 0.4 + ratio * 0.5);
    pad.strokeEllipse(cx, cy + 116, 230, 48);
    container.add(pad);

    // 後方發光圈(完成度越高越強、越大)— 呼吸 tween 讓基地「活著」。
    const halo = this.add.graphics();
    const haloR = 132 + ratio * 26;
    halo.fillStyle(pal.glow, 0.10 + ratio * 0.22);
    halo.fillCircle(cx, cy + 6, haloR + 18);
    halo.fillStyle(pal.glow, 0.14 + ratio * 0.26);
    halo.fillCircle(cx, cy + 6, haloR);
    container.add(halo);
    try {
      this.tweens.add({
        targets: halo, alpha: 0.55 + ratio * 0.35, scaleX: 1.05, scaleY: 1.05,
        duration: 1700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    } catch { /* headless */ }

    // 基地主圖(AI)。完成度越高 → tint 由暗(未升)轉白(全亮),做整體 glow-up。
    const key = BASE_IMG_KEY[this.heroId];
    let placed = false;
    try {
      if (this.textures.exists(key)) {
        const img = this.add.image(cx, cy, key).setOrigin(0.5);
        const tw = img.width || 1;
        const th = img.height || 1;
        // 縮到合理高度(基地約佔畫面中段 ~240 高,保留長寬比)。
        const targetH = 246;
        img.setDisplaySize(targetH * (tw / th), targetH);
        // 亮度:0% 完成 = 暗(深灰 tint),100% = 原色全亮(白 tint)。
        const shade = Math.round(80 + ratio * 175); // 80..255
        img.setTint(Phaser.Display.Color.GetColor(shade, shade, shade));
        container.add(img);
        placed = true;
        // 進場:輕微上浮 + 淡入。
        try {
          img.setAlpha(0); img.y = cy + 10;
          this.tweens.add({ targets: img, alpha: 1, y: cy, duration: 320, ease: 'Cubic.easeOut' });
        } catch { /* headless */ }
      }
    } catch { /* headless */ }

    if (!placed) {
      // fallback:門派色基地剪影方塊(缺圖 / headless)。
      const g = this.add.graphics();
      const shade = 0.4 + ratio * 0.6;
      g.fillStyle(pal.primary, shade);
      g.fillRoundedRect(cx - 90, cy - 90, 180, 180, 18);
      g.lineStyle(3, pal.light, 0.8);
      g.strokeRoundedRect(cx - 90, cy - 90, 180, 180, 18);
      g.fillStyle(pal.glow, ratio * 0.6);
      g.fillRoundedRect(cx - 90, cy - 90, 180, 60, { tl: 18, tr: 18, bl: 0, br: 0 });
      container.add(g);
      container.add(this.add.text(cx, cy, pal.emoji, { fontSize: '64px', fontFamily: 'sans-serif' })
        .setOrigin(0.5));
    }

    // 基地名牌(圖上方)。
    const nameBg = this.add.graphics();
    nameBg.fillStyle(0x000000, 0.45);
    nameBg.fillRoundedRect(cx - 96, cy - 150, 192, 30, 14);
    container.add(nameBg);
    container.add(this.add.text(cx, cy - 135, `${pal.emoji} ${pal.name}`, {
      fontSize: '16px', color: '#fff3d6', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0.5));

    // 完成度條(基地下方)。
    const barW = 232;
    const barY = cy + 146;
    const bx = cx - barW / 2;
    const bar = this.add.graphics();
    bar.fillStyle(0x000000, 0.5);
    bar.fillRoundedRect(bx, barY, barW, 14, 7);
    bar.fillStyle(pal.primary, 1);
    if (pct > 0) bar.fillRoundedRect(bx, barY, Math.max(14, barW * (pct / 100)), 14, 7);
    bar.lineStyle(1.5, pal.light, 0.8);
    bar.strokeRoundedRect(bx, barY, barW, 14, 7);
    container.add(bar);
    container.add(this.add.text(cx, barY - 12, `基地完成度 ${pct}%`, {
      fontSize: '13px', color: '#e8dcff', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0.5));

    // 全滿 → 「✦ 基地全亮」標。
    if (pct >= 100) {
      container.add(this.add.text(cx, barY + 30, '✦ 基地全面點亮!', {
        fontSize: '14px', color: '#ffe9a8', fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5));
    }
  }

  // ── 右側節點清單面板:每個升級節點一行(名/加成/成本/購買鈕)──────────────────
  private buildList(): void {
    if (this.listContainer) { this.listContainer.destroy(); this.listContainer = null; }

    const W = GAME.width;
    const tree = metaTreeFor(this.heroId);
    const progress = loadProgress();
    const coins = this.currentCoins();
    const pal = BASE_PALETTES[this.heroId];

    const panelX = W * 0.58;
    const panelW = W - panelX - 18;
    const panelTop = 150;
    const rowH = 84;
    const rowGap = 10;

    const c = this.add.container(0, 0).setDepth(30);
    this.listContainer = c;

    // 面板標題。
    c.add(this.add.text(panelX + panelW / 2, panelTop - 18, '升級節點', {
      fontSize: '15px', color: '#c9badf', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0.5));

    tree.nodes.forEach((node, i) => {
      const y = panelTop + i * (rowH + rowGap);
      const level = progress ? metaNodeLevel(progress, this.heroId, node.id) : 0;
      const maxed = level >= node.maxLevel;
      const nextCost = maxed ? 0 : metaNodeCostAtLevel(node.cost1, level + 1);
      const affordable = !maxed && coins >= nextCost;

      // 行卡。
      const g = this.add.graphics();
      g.fillStyle(0x18112e, 0.94);
      g.fillRoundedRect(panelX, y, panelW, rowH, 12);
      g.fillStyle(pal.primary, 0.85);
      g.fillRoundedRect(panelX, y, 5, rowH, { tl: 12, tr: 0, bl: 12, br: 0 });
      g.lineStyle(1.5, maxed ? pal.glow : pal.light, maxed ? 0.8 : 0.45);
      g.strokeRoundedRect(panelX, y, panelW, rowH, 12);
      c.add(g);

      const padX = panelX + 16;
      // 節點名。
      c.add(this.add.text(padX, y + 12, `${pal.emoji} ${node.label}`, {
        fontSize: '17px', color: '#ffffff', fontFamily: 'sans-serif', fontStyle: 'bold',
      }));
      // 每級加成。
      const perPct = Math.round(node.perLevel * 100);
      c.add(this.add.text(padX, y + 36, `${STAT_LABEL[node.stat]} · 每級 +${perPct}%`, {
        fontSize: '13px', color: '#c9badf', fontFamily: 'sans-serif',
      }));
      // 等級進度(小圓點)。
      this.buildLevelDots(c, padX, y + 60, level, node.maxLevel, pal);

      // 右側:成本 + 購買鈕 / 已滿。
      const btnW = 96;
      const btnH = 38;
      const btnCx = panelX + panelW - btnW / 2 - 14;
      const btnCy = y + rowH / 2;
      if (maxed) {
        const tag = this.add.graphics();
        tag.fillStyle(0x1b3a22, 0.9);
        tag.fillRoundedRect(btnCx - btnW / 2, btnCy - btnH / 2, btnW, btnH, 10);
        tag.lineStyle(2, 0x66bb6a, 0.9);
        tag.strokeRoundedRect(btnCx - btnW / 2, btnCy - btnH / 2, btnW, btnH, 10);
        c.add(tag);
        c.add(this.add.text(btnCx, btnCy, '✦ 已滿', {
          fontSize: '15px', color: '#9be0a4', fontFamily: 'sans-serif', fontStyle: 'bold',
        }).setOrigin(0.5));
      } else {
        // 成本標(鈕上方)。
        c.add(this.add.text(btnCx, y + 13, `🪙 ${nextCost}`, {
          fontSize: '14px', color: affordable ? '#ffd54f' : '#8a8a96',
          fontFamily: 'sans-serif', fontStyle: 'bold',
        }).setOrigin(0.5, 0));
        this.buildBuyButton(
          c, btnCx, y + rowH - 24, btnW, btnH,
          affordable ? '升級' : '不足',
          affordable ? pal.primary : 0x3a3344,
          affordable ? pal.light : 0x555555,
          affordable ? () => this.buy(node, i) : undefined,
          affordable ? '#1a0d2e' : '#888888',
        );
      }
    });
  }

  /** 節點清單行的等級小圓點(亮=已升,暗=尚可升,滿級加星)。 */
  private buildLevelDots(
    parent: Phaser.GameObjects.Container,
    x: number, y: number, level: number, maxLevel: number, pal: BasePalette,
  ): void {
    const n = Math.max(1, maxLevel);
    const gap = 14;
    for (let i = 0; i < n; i++) {
      const dx = x + i * gap;
      const on = i < level;
      const dot = this.add.graphics();
      if (on) {
        dot.fillStyle(pal.light, 1);
        dot.fillCircle(dx, y, 5);
        dot.lineStyle(1.5, 0xffffff, 0.8);
        dot.strokeCircle(dx, y, 5);
      } else {
        dot.fillStyle(0x000000, 0.4);
        dot.fillCircle(dx, y, 4);
        dot.lineStyle(1.5, pal.primary, 0.5);
        dot.strokeCircle(dx, y, 4);
      }
      parent.add(dot);
    }
    parent.add(this.add.text(x + n * gap + 6, y, `Lv ${level}/${maxLevel}`, {
      fontSize: '12px', color: '#ffd54f', fontFamily: 'sans-serif',
    }).setOrigin(0, 0.5));
  }

  /** 清單行內的購買鈕(置中於 (cx, cy))。 */
  private buildBuyButton(
    parent: Phaser.GameObjects.Container,
    cx: number, cy: number, w: number, h: number,
    label: string, color: number, stroke: number,
    onClick: (() => void) | undefined, textColor: string,
  ): void {
    const g = this.add.graphics();
    g.fillStyle(color, 1);
    g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, 10);
    g.lineStyle(2, stroke, 0.9);
    g.strokeRoundedRect(cx - w / 2, cy - h / 2, w, h, 10);
    parent.add(g);
    parent.add(this.add.text(cx, cy, label, {
      fontSize: '15px', color: textColor, fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0.5));

    if (!onClick) return;
    const hit = this.add.zone(cx, cy, w, h).setOrigin(0.5);
    hit.setInteractive({ useHandCursor: true });
    hit.on('pointerup', onClick);
    parent.add(hit);
  }

  // ── 購買 / 洗點(沿用既有純函式;鐵則:只寫 metaCoins / metaShop)─────────────
  private buy(node: MetaStatNode, _index: number): void {
    const progress = loadProgress();
    if (!progress) return;
    const level = metaNodeLevel(progress, this.heroId, node.id);
    if (level >= node.maxLevel) return;
    const cost = metaNodeCostAtLevel(node.cost1, level + 1);
    const coins = Number.isFinite(progress.metaCoins) ? (progress.metaCoins as number) : 0;
    if (coins < cost) return;

    progress.metaCoins = coins - cost;
    setMetaNodeLevel(progress, this.heroId, node.id, level + 1);
    saveProgress(progress);

    // 「蓋好了」視覺反饋:金幣更新 + 基地中央冒星 + 基地重建(整體更亮)+ 清單刷新。
    this.updateCoinText();
    this.emitBuildBurst();
    this.buildBase();
    this.buildList();
  }

  /** 購買成功在基地中央冒一圈擴散光環 + 星花,強化「基地長大/更亮」成就感。 */
  private emitBuildBurst(): void {
    const W = GAME.width;
    const H = GAME.height;
    const cx = W * 0.30;
    const cy = H * 0.56;
    const pal = BASE_PALETTES[this.heroId];

    const ring = this.add.graphics().setDepth(70);
    ring.lineStyle(4, pal.glow, 0.9);
    ring.strokeCircle(cx, cy, 90);
    try {
      this.tweens.add({
        targets: ring, scaleX: 1.8, scaleY: 1.8, alpha: 0,
        duration: 600, ease: 'Cubic.easeOut',
        onComplete: () => { try { ring.destroy(); } catch { /* noop */ } },
      });
    } catch { try { ring.destroy(); } catch { /* noop */ } }
    // 一圈飛濺星花。
    for (let k = 0; k < 10; k++) {
      const a = (k / 10) * Math.PI * 2 + Math.random() * 0.3;
      const star = this.add.text(cx, cy, '✦', {
        fontSize: '18px', color: '#ffe9a8', fontFamily: 'sans-serif',
      }).setOrigin(0.5).setDepth(71);
      try {
        this.tweens.add({
          targets: star,
          x: cx + Math.cos(a) * 110,
          y: cy + Math.sin(a) * 110,
          alpha: 0, scale: 0.4,
          duration: 560, ease: 'Cubic.easeOut',
          onComplete: () => { try { star.destroy(); } catch { /* noop */ } },
        });
      } catch { try { star.destroy(); } catch { /* noop */ } }
    }
  }

  /** 洗點:退還當前門派整棵樹「已花成本」的 70%,清空該樹等級。 */
  private respec(): void {
    const progress = loadProgress();
    if (!progress) return;
    const tree = metaTreeFor(this.heroId);
    let spent = 0;
    for (const node of tree.nodes) {
      const level = metaNodeLevel(progress, this.heroId, node.id);
      spent += metaNodeTotalCost(node.cost1, 0, level);
    }
    if (spent <= 0) return;
    const refund = metaRespecRefund(spent);
    const coins = Number.isFinite(progress.metaCoins) ? (progress.metaCoins as number) : 0;
    progress.metaCoins = coins + refund;
    for (const node of tree.nodes) {
      setMetaNodeLevel(progress, this.heroId, node.id, 0);
    }
    saveProgress(progress);

    this.updateCoinText();
    this.buildBase();
    this.buildList();
    this.flashToast(`已洗點 · 退回 🪙 ${refund}`);
  }

  /** 短暫 toast(置中下方,2s 淡出)。 */
  private flashToast(msg: string): void {
    const t = this.add.text(GAME.width / 2, GAME.height - 80, msg, {
      fontSize: '15px', color: '#ffffff', fontFamily: 'sans-serif', fontStyle: 'bold',
      backgroundColor: '#3d2a66', padding: { x: 14, y: 8 },
    }).setOrigin(0.5).setDepth(80);
    try {
      this.tweens.add({
        targets: t, alpha: 0, y: GAME.height - 100, duration: 1800, delay: 400,
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
    } catch { try { t.destroy(); } catch { /* noop */ } }
  }

  // ── 角落圓角按鈕(返回 / 洗點)───────────────────────────────────────────────
  private buildCornerButton(
    cx: number, cy: number, w: number, h: number, label: string,
    color: number, stroke: number, onClick: () => void,
  ): void {
    const g = this.add.graphics().setDepth(50);
    g.fillStyle(color, 1);
    g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, 12);
    g.lineStyle(2, stroke, 0.85);
    g.strokeRoundedRect(cx - w / 2, cy - h / 2, w, h, 12);
    this.add.text(cx, cy, label, {
      fontSize: '14px', color: '#fff8e1', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(51);
    const hit = this.add.zone(cx, cy, w, h).setOrigin(0.5).setDepth(51);
    hit.setInteractive({ useHandCursor: true });
    hit.on('pointerup', onClick);
  }

  /** 返回 returnScene。 */
  private gotoReturnScene(): void {
    try {
      this.scene.start(this.returnScene);
    } catch { /* headless */ }
  }
}

/** 給 boot.ts 註冊用:導出所有 meta 樹 id 供 e2e/測試 sanity。 */
export const META_SHOP_HERO_IDS = Object.keys(META_HERO_TREES) as FactionId[];
